import { useCallback, useEffect, useRef, useState } from 'react';
import type { ImageAttachment } from '@localagent/sdk';
import { getSDK } from '../services/sdk-instance';
import type { ImageAttachmentChip } from '../components/chat/ImageAttachmentChips';
import type {
  PrepareImageRequest,
  PrepareImageResponse,
} from './image-prepare.worker';

/** MIME types the composer accepts, matching the server's accepted set. */
export const ACCEPTED_IMAGE_TYPES = [
  'image/png',
  'image/jpeg',
  'image/gif',
  'image/webp',
] as const;

/** Server-side per-image byte cap the worker loop targets. */
const MAX_IMAGE_BYTES = 5 * 1024 * 1024;

/** Server-side pixel budget. */
const MAX_IMAGE_PIXELS = 40_000_000;

/** Server-enforced images-per-message cap; refused here so it is visible early. */
const MAX_IMAGES_PER_MESSAGE = 10;

export interface UseImageAttachmentsOptions {
  /**
   * Resolves the session the images belong to, CREATING it when the chat is
   * still a draft. Images upload on attach, so the first attachment is what
   * materializes the session — a draft has no id to upload against.
   */
  ensureSession: () => Promise<{ sessionId: string; workspaceId: string | null }>;
}

export interface UseImageAttachments {
  attachments: ImageAttachmentChip[];
  /** Refusal text for a non-image or over-count attach; null when there is none. */
  refusal: string | null;
  dismissRefusal: () => void;
  attachFiles: (files: File[], source: 'paste' | 'upload') => void;
  removeAttachment: (id: string) => void;
  retryAttachment: (id: string) => void;
  clearAttachments: () => void;
  /** Take the chips out of the composer for a send; their previews stay alive until released or restored. */
  detachAttachments: () => ImageAttachmentChip[];
  /** Put detached chips back in front of anything attached since, e.g. after a refused send. */
  restoreAttachments: (chips: ImageAttachmentChip[]) => void;
  /** Free detached chips' previews once their send was accepted. */
  releaseAttachments: (chips: ImageAttachmentChip[]) => void;
  /** The wire entries for `ChatSession.send`, in attach order. */
  readyAttachments: ImageAttachment[];
  /** True while any chip is preparing, uploading, or failed — send must wait. */
  hasUnsentAttachments: boolean;
}

interface PendingUpload {
  chipId: string;
  label: string;
  source: 'paste' | 'upload';
  file: File;
}

function isAcceptedType(type: string): boolean {
  return (ACCEPTED_IMAGE_TYPES as readonly string[]).includes(type);
}

export function useImageAttachments(opts: UseImageAttachmentsOptions): UseImageAttachments {
  const { ensureSession } = opts;
  const [attachments, setAttachments] = useState<ImageAttachmentChip[]>([]);
  const [refusal, setRefusal] = useState<string | null>(null);

  const workerRef = useRef<Worker | null>(null);
  const pendingRef = useRef(new Map<string, PendingUpload>());
  const previewUrlsRef = useRef(new Set<string>());
  const detachedUrlsRef = useRef(new Set<string>());
  const pasteCounterRef = useRef(0);
  // Mirrors `attachments` so attachFiles can read the live count without
  // computing the per-message cap inside a state updater (an updater may run
  // twice, and the refusal message is a side effect).
  const attachmentsRef = useRef<ImageAttachmentChip[]>([]);
  attachmentsRef.current = attachments;

  const patchChip = useCallback((id: string, patch: Partial<ImageAttachmentChip>) => {
    setAttachments((prev) =>
      prev.map((chip) => (chip.id === id ? { ...chip, ...patch } : chip)),
    );
  }, []);

  const upload = useCallback(
    async (chipId: string, bytes: Blob, label: string, source: 'paste' | 'upload') => {
      const { sessionId, workspaceId } = await ensureSession();
      const sdk = getSDK();
      const uploaded = await sdk.sessions.uploadImage(
        sessionId,
        bytes,
        label,
        workspaceId ?? undefined,
        source,
      );
      // (sdk ADR-b0123)
      await sdk.chat.queuedImages.keep(sessionId, uploaded.image_id, bytes);
      patchChip(chipId, {
        status: 'ready',
        imageId: uploaded.image_id,
        width: uploaded.width,
        height: uploaded.height,
        sizeBytes: uploaded.size_bytes,
        error: undefined,
      });
    },
    [ensureSession, patchChip],
  );

  const worker = useCallback((): Worker => {
    if (workerRef.current === null) {
      workerRef.current = new Worker(
        new URL('./image-prepare.worker.ts', import.meta.url),
        { type: 'module' },
      );
      workerRef.current.onmessage = (event: MessageEvent<PrepareImageResponse>) => {
        const res = event.data;
        const pending = pendingRef.current.get(res.id);
        if (!pending) return;
        if (!res.ok) {
          // The pending entry STAYS so Retry can re-run preparation on the
          // same file (removed only by removeAttachment / clearAttachments).
          patchChip(res.id, { status: 'error', error: res.error });
          return;
        }
        patchChip(res.id, { status: 'uploading' });
        void upload(res.id, new Blob([res.bytes], { type: res.type }), pending.label, pending.source)
          .catch((err: unknown) => {
            patchChip(res.id, {
              status: 'error',
              error: err instanceof Error ? err.message : 'upload failed',
            });
          });
      };
      workerRef.current.onerror = (event: ErrorEvent) => {
        // A worker that cannot start is a real failure, reported on every chip
        // still waiting on it rather than degrading to main-thread encoding.
        for (const [chipId] of pendingRef.current) {
          patchChip(chipId, {
            status: 'error',
            error: event.message || 'image preparation worker failed',
          });
        }
      };
    }
    return workerRef.current;
  }, [patchChip, upload]);

  const startPreparation = useCallback(
    (pending: PendingUpload) => {
      pendingRef.current.set(pending.chipId, pending);
      void pending.file
        .arrayBuffer()
        .then((buffer) => {
          const req: PrepareImageRequest = {
            id: pending.chipId,
            bytes: buffer,
            type: pending.file.type,
            maxBytes: MAX_IMAGE_BYTES,
            maxPixels: MAX_IMAGE_PIXELS,
          };
          worker().postMessage(req, [buffer]);
        })
        .catch((err: unknown) => {
          patchChip(pending.chipId, {
            status: 'error',
            error: err instanceof Error ? err.message : 'could not read the picked file',
          });
        });
    },
    [patchChip, worker],
  );

  const attachFiles = useCallback(
    (files: File[], source: 'paste' | 'upload') => {
      const accepted: File[] = [];
      const rejected: string[] = [];
      for (const file of files) {
        if (isAcceptedType(file.type)) accepted.push(file);
        else rejected.push(file.name || file.type || 'unnamed file');
      }

      const room = Math.max(0, MAX_IMAGES_PER_MESSAGE - attachmentsRef.current.length);
      const admitted = accepted.slice(0, room);
      const overflow = accepted.length - admitted.length;

      const messages: string[] = [];
      if (rejected.length > 0) {
        messages.push(
          `Only PNG, JPEG, single-frame GIF and WebP images can be attached — refused ${rejected.join(', ')}.`,
        );
      }
      if (overflow > 0) {
        messages.push(
          `A message can carry at most ${MAX_IMAGES_PER_MESSAGE} images — ${overflow} not attached.`,
        );
      }
      setRefusal(messages.length > 0 ? messages.join(' ') : null);
      if (admitted.length === 0) return;

      const chips: ImageAttachmentChip[] = [];
      for (const file of admitted) {
        const chipId = `img-${Date.now()}-${Math.random().toString(36).slice(2)}`;
        pasteCounterRef.current += 1;
        const label =
          file.name && file.name !== '' ? file.name : `Pasted image ${pasteCounterRef.current}`;
        const previewUrl = URL.createObjectURL(file);
        previewUrlsRef.current.add(previewUrl);
        chips.push({ id: chipId, label, previewUrl, status: 'preparing' });
        startPreparation({ chipId, label, source, file });
      }
      setAttachments((prev) => [...prev, ...chips]);
    },
    [startPreparation],
  );

  const removeAttachment = useCallback((id: string) => {
    pendingRef.current.delete(id);
    const chip = attachmentsRef.current.find((c) => c.id === id);
    if (chip) {
      URL.revokeObjectURL(chip.previewUrl);
      previewUrlsRef.current.delete(chip.previewUrl);
      if (chip.imageId) getSDK().chat.queuedImages.settle(chip.imageId);
    }
    setAttachments((prev) => prev.filter((c) => c.id !== id));
  }, []);

  const retryAttachment = useCallback(
    (id: string) => {
      const pending = pendingRef.current.get(id);
      if (!pending) return;
      patchChip(id, { status: 'preparing', error: undefined });
      startPreparation(pending);
    },
    [patchChip, startPreparation],
  );

  const clearAttachments = useCallback(() => {
    for (const chip of attachmentsRef.current) {
      if (chip.imageId) getSDK().chat.queuedImages.settle(chip.imageId);
    }
    pendingRef.current.clear();
    for (const url of previewUrlsRef.current) URL.revokeObjectURL(url);
    previewUrlsRef.current.clear();
    setAttachments([]);
    setRefusal(null);
  }, []);

  const detachAttachments = useCallback((): ImageAttachmentChip[] => {
    const detached = attachmentsRef.current;
    for (const chip of detached) {
      pendingRef.current.delete(chip.id);
      previewUrlsRef.current.delete(chip.previewUrl);
      detachedUrlsRef.current.add(chip.previewUrl);
    }
    attachmentsRef.current = [];
    setAttachments([]);
    setRefusal(null);
    return detached;
  }, []);

  const restoreAttachments = useCallback((chips: ImageAttachmentChip[]) => {
    if (chips.length === 0) return;
    for (const chip of chips) {
      detachedUrlsRef.current.delete(chip.previewUrl);
      previewUrlsRef.current.add(chip.previewUrl);
    }
    const combined = [...chips, ...attachmentsRef.current];
    const kept = combined.slice(0, MAX_IMAGES_PER_MESSAGE);
    const dropped = combined.slice(MAX_IMAGES_PER_MESSAGE);
    for (const chip of dropped) {
      pendingRef.current.delete(chip.id);
      URL.revokeObjectURL(chip.previewUrl);
      previewUrlsRef.current.delete(chip.previewUrl);
      if (chip.imageId) getSDK().chat.queuedImages.settle(chip.imageId);
    }
    if (dropped.length > 0) {
      setRefusal(
        `A message can carry at most ${MAX_IMAGES_PER_MESSAGE} images — ${dropped.length} attached during the send were removed.`,
      );
    }
    attachmentsRef.current = kept;
    setAttachments(kept);
  }, []);

  const releaseAttachments = useCallback((chips: ImageAttachmentChip[]) => {
    for (const chip of chips) {
      URL.revokeObjectURL(chip.previewUrl);
      detachedUrlsRef.current.delete(chip.previewUrl);
    }
  }, []);

  useEffect(
    () => () => {
      workerRef.current?.terminate();
      workerRef.current = null;
      for (const url of previewUrlsRef.current) URL.revokeObjectURL(url);
      previewUrlsRef.current.clear();
      for (const url of detachedUrlsRef.current) URL.revokeObjectURL(url);
      detachedUrlsRef.current.clear();
    },
    [],
  );

  const readyAttachments: ImageAttachment[] = attachments
    .filter((chip) => chip.status === 'ready' && chip.imageId !== undefined)
    .map((chip) => ({ type: 'image', image_id: chip.imageId as string, label: chip.label }));

  return {
    attachments,
    refusal,
    dismissRefusal: () => setRefusal(null),
    attachFiles,
    removeAttachment,
    retryAttachment,
    clearAttachments,
    detachAttachments,
    restoreAttachments,
    releaseAttachments,
    readyAttachments,
    hasUnsentAttachments: attachments.some((chip) => chip.status !== 'ready'),
  };
}
